import React, { createContext, useContext, useRef, useState, type ReactNode, type RefObject } from "react";
import { StyleSheet, View } from "react-native";
import { colors, semanticColors } from "../styles/design-system";
import { useMathVirtualKeyboard } from "../hooks/useMathVirtualKeyboard";

export interface MathVirtualKeyboardContextValue {
  virtualKeyboardVisible: boolean;
  setVirtualKeyboardVisible: (visible: boolean) => void;
  toggleVirtualKeyboard: () => void;
  virtualKeyboardContainerRef: RefObject<View | null>;
}

const MathVirtualKeyboardContext = createContext<MathVirtualKeyboardContextValue | undefined>(undefined);

/**
 * Wrap any screen that uses the chat composer with math mode so the virtual keyboard
 * has a host container. Use MathVirtualKeyboardProvider and place it around the content
 * that contains ChatComposerBar.
 */
interface MathVirtualKeyboardProviderProps {
  children: ReactNode;
}

export function MathVirtualKeyboardProvider({ children }: MathVirtualKeyboardProviderProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<View>(null);
  const { toggleVirtualKeyboard } = useMathVirtualKeyboard({
    containerRef,
    open,
    setOpen,
  });

  const value: MathVirtualKeyboardContextValue = {
    virtualKeyboardVisible: open,
    setVirtualKeyboardVisible: setOpen,
    toggleVirtualKeyboard,
    virtualKeyboardContainerRef: containerRef,
  };

  return (
    <MathVirtualKeyboardContext.Provider value={value}>
      {children}
      <View
        ref={containerRef}
        style={[styles.keyboardContainer, open && styles.keyboardContainerOpen]}
        collapsable={false}
      />
    </MathVirtualKeyboardContext.Provider>
  );
}

const styles = StyleSheet.create({
  keyboardContainer: {
    width: "100%",
    minHeight: 0,
    overflow: "visible" as const,
    backgroundColor: semanticColors.surface.light,
  },
  keyboardContainerOpen: {
    minHeight: 320,
    borderTopWidth: 1,
    borderTopColor: colors.grey[200],
  },
});

/** Returns undefined when outside provider so Storybook/tests can pass props instead. */
export function useMathVirtualKeyboardContext(): MathVirtualKeyboardContextValue | undefined {
  return useContext(MathVirtualKeyboardContext);
}
